import { FC, useMemo, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { TableState } from '@/services/table/tableState';
import { useDispatch, useSelector } from 'react-redux';
import { ReducerState } from '@/services/common/lib/reducerState';
import { PokerCategory } from '@/services/common/types/pokerCategory';
import useIsMobileView from '@/hooks/useIsMobileView';
import BackgroundTable, { Variant } from '@/features/poker-table/components/BackgroundTable/BackgroundTable';
import TableSeatStructureMobile from '@/features/poker-table/utils/TableSeatStructureMobile';
import TableSeatStructure from '@/features/poker-table/utils/TableSeatStructure';
import UserPanel from '@/features/poker-table/components/UserPanel/UserPanel';
import OpenSeat from '@/features/poker-table/components/OpenSeat/OpenSeat';
import JoinSeat from '@/features/poker-table/components/JoinSeat/JoinSeat';
import Board from '@/features/poker-table/components/Board/Board';
import styles from './TablePlay.module.css';
import { useRootFontSize } from '@/hooks/useRootFontSize';
import { cancelReserveTableRest, reserveSeat, sitOutRest } from '@/services/table/tableService';
import {
  LobbyTableCloseBuyInAction,
  LobbyTableOpenBuyInAction,
  LobbyTableUpdateBuyInAction,
} from '@/services/lobby/lobbyActions';
import BuyIn from '@/pages/modals/components/BuyIn';
import { PositionProvider } from './context/PositionContext';

const getBoardCards = (category: PokerCategory | undefined) => {
  if (!category) return 0;

  if (category === PokerCategory.HOLDEM || category === PokerCategory.OMAHA) {
    return 5;
  }
  return 0;
};

const TablePlay: FC<{ tableState: TableState }> = ({ tableState }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tableBuyIn = useSelector((state: ReducerState) => state.lobby.tableBuyIn);

  useRootFontSize(wrapperRef);
  const isMobileView = useIsMobileView({ checkForUserAgent: true });
  const dispatch = useDispatch();
  // TODO
  // const isPortrait = useIsPortraitView()
  const user = useSelector((state: ReducerState) => state.auth);
  const backgroundDesign = user.settings?.backgroundDesign;
  const feltColor = user.settings?.feltColor || 'purple';
  const userId = user.id || null;
  const tableId = tableState?.tableId;

  const configuration = useMemo(
    () =>
      isMobileView
        ? TableSeatStructureMobile.get(tableState?.maxPlayers)
        : TableSeatStructure.get(tableState?.maxPlayers),
    [isMobileView, tableState?.maxPlayers],
  );
  if (!userId) {
    throw new Error('No userId');
  }

  if (!tableId) {
    return null;
  }

  const playerSeat = tableState?.playerSeat;

  const tableVariant =
    typeof playerSeat?.seat === 'number' && tableState.winnersData?.playerIndexes.includes(playerSeat?.seat)
      ? Variant.WinPot
      : user.settings?.backgroundDesign === Variant.Default || !user.settings?.backgroundDesign
        ? Variant.Default
        : Variant.Custom;

  const relativePosition = (seat?: number) => {
    if (seat === undefined) {
      return undefined;
    }
    if (!tableState?.maxPlayers || !playerSeat?.seat) {
      return seat;
    }

    if (seat === playerSeat.seat) {
      return 0;
    }

    return seat > playerSeat.seat ? seat - playerSeat.seat : tableState.maxPlayers - playerSeat.seat + seat;
  };
  if (!tableState) {
    return null;
  }

  const relativePositions = tableState.seats.map((seat) => relativePosition(seat?.seat));

  const inverseRelativePosition = (relativePos: number): number => {
    if (!tableState?.maxPlayers || !playerSeat?.seat) {
      return relativePos;
    }
    let originalSeat = playerSeat.seat + relativePos;

    if (originalSeat >= tableState.maxPlayers) {
      originalSeat -= tableState.maxPlayers;
    }
    return originalSeat;
  };

  const isSeatOccupied = (seatPosition: number): boolean => {
    return relativePositions.includes(seatPosition);
  };

  const handleJoinTable = async (seat: number) => {
    // stack coming is the existing session stack
    dispatch(
      LobbyTableOpenBuyInAction({
        tableId,
        seat,
        stack: 0,
      }),
    );
    const {
      data: { existingSessionStack, txId },
    } = await reserveSeat({ tableId, seat, tableUrl: tableState?.url });

    dispatch(LobbyTableUpdateBuyInAction(tableId, seat, existingSessionStack || 0, txId, true));
  };

  const state = tableState?.handState?.state;
  const isDrawStreet =
    !!state && state.stander_pat_or_discarder_index !== null && state.stander_pat_or_discarder_index !== undefined;
  const isLastStreet = tableState.handState?.state?.street_index
    ? tableState?.handState?.state?.streets?.length === tableState.handState?.state?.street_index + 1 && !isDrawStreet
    : false;

  return (
    <div className={styles.tableWrapper} ref={wrapperRef}>
      <PositionProvider
        overrideInitialState={{
          tableRef: wrapperRef,
          userChipsPositions: {},
          potPosition: { x: 0, y: 0 },
        }}
      >
        <div id="TableContainer" className={styles.tableContainer}>
          <BackgroundTable
            showLogo={getBoardCards(tableState.currentVariant?.category) === 0}
            variant={tableVariant}
            customImageSrc={backgroundDesign}
            feltColor={feltColor}
          />

          <div className="absolute flex h-full w-full flex-col items-center justify-between">
            {configuration &&
              configuration.map((item, index) => {
                const isPlayerSeat = index === configuration.length - 1;
                return (
                  <div
                    key={index}
                    className={twMerge(
                      'relative',
                      'flex items-center',
                      item.classes,
                      isPlayerSeat ? 'justify-center' : 'justify-between',
                    )}
                  >
                    {item.config.map((seatConfig) => {
                      const seatNumber = inverseRelativePosition(seatConfig.seatPosition);
                      if (isSeatOccupied(seatConfig.seatPosition)) {
                        const seat = tableState.seats[seatNumber];
                        const self = seat?.userId === userId;
                        if (seat && !(self && playerSeat?.leftTable)) {
                          return (
                            <UserPanel
                              key={seatNumber}
                              bigBlind={tableState?.bigBlind}
                              userId={seat.userId || ''}
                              self={self}
                              seat={seat}
                              isDealer={seat.isDealer}
                              alias={seat.alias || ''}
                              tableId={tableState.tableId}
                              winningData={tableState?.winnersData}
                              holeCards={seat.holeCards || []}
                              playerBet={seat.betAmount}
                              stack={seat.stack}
                              betPosition={seatConfig.betPosition}
                              lastOperationLabel={
                                tableState?.lastOperationLabel?.playerIndex === seat.playerIndex
                                  ? tableState?.lastOperationLabel
                                  : undefined
                              }
                              isDisconnected={seat.isActingPlayer && seat.active && seat.disconnected}
                              tableState={tableState}
                            />
                          );
                        } else if (seat && seat.leftTable) {
                          return <OpenSeat />;
                        } else {
                          return playerSeat ? (
                            <OpenSeat />
                          ) : (
                            <JoinSeat
                              disabled={false}
                              seatNumber={seatNumber}
                              onClickSeat={() => {
                                handleJoinTable(seatNumber);
                              }}
                            />
                          );
                        }
                      }
                      return (
                        <div className={styles.openSeat}>
                          {playerSeat ? (
                            <OpenSeat />
                          ) : (
                            <JoinSeat
                              disabled={false}
                              seatNumber={seatNumber}
                              onClickSeat={() => {
                                handleJoinTable(seatNumber);
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
          </div>
          <div className="absolute flex w-full justify-center">
            <Board
              isInitializing={!tableState.url}
              isDrawStreet={isDrawStreet}
              isLastStreet={isLastStreet}
              bigBlind={tableState?.bigBlind}
              winnerName={tableState.winnerSeat ? tableState.seats[tableState.winnerSeat]?.alias : undefined}
              winnerBet={tableState.winnerBetAmount}
              cards={tableState.handState?.state?.board_cards.map((c) => c[0]) || []}
              pot={tableState.handState?.state?.totalPot || 0}
              boardCardsCount={getBoardCards(tableState.currentVariant?.category)}
              currentVariant={tableState.currentVariant?.name || ''}
              variants={tableState.variants}
              game={tableState.name}
              handsInCurrentGame={tableState.handsInCurrentGame}
              handsPerGame={tableState.handsPerGame}
              currentDraw={tableState.currentDraw}
              totalDraws={
                //If there is no hand state don't show anything
                tableState.handState ? tableState.totalDraws : undefined
              }
              pots={tableState.handState?.state?.pots}
              winningData={tableState?.winnersData}
              tableId={tableState.tableId}
              numberOfPlayers={tableState.maxPlayers}
            />
          </div>
          {tableBuyIn?.open && (
            <BuyIn
              isOpen={tableBuyIn?.open}
              showTimer={tableBuyIn?.showTimer}
              isRebuy={tableBuyIn?.isRebuy}
              onClose={async () => {
                dispatch(LobbyTableCloseBuyInAction(tableId));

                if (!tableBuyIn?.isRebuy) {
                  await cancelReserveTableRest({
                    tableId: tableBuyIn.tableId,
                    seat: tableBuyIn.seat,
                    tableUrl: tableState?.url,
                  });
                  return;
                }

                if (tableBuyIn?.sitOutOnCancel) {
                  await sitOutRest({ tableId, tableUrl: tableState?.url });
                }
              }}
              seat={tableBuyIn?.seat}
              stack={tableBuyIn?.stack}
              tableId={tableBuyIn?.tableId}
              seatReserved={!!tableBuyIn?.seatReserved}
              txId={tableBuyIn?.txId}
            />
          )}
        </div>
      </PositionProvider>
    </div>
  );
};

export default TablePlay;
